import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import LoadingSpinner from "@client/components/utils/LoadingSpinner";
import { Page } from "@client/components/utils/Page";
import { RoundWizard } from "@client/components/wizard/RoundWizard";
import { useCurrentUser } from "@client/hooks/use-current-user";
import { useImpersonationRefValue } from "@client/ImperativeState";
import {
  isIPadOffline,
  isIPadOfflineFailure,
  OFFLINE_CACHE_KEYS,
  readOfflineCache,
  writeOfflineCacheIfIPad,
} from "@client/offline/offlineCache";
import { trpc } from "@client/trpc";
import { isDemoRoundName } from "@lib/constants";
import {
  Button,
  Card,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Input,
  useLocalStorage,
} from "@sfc/ui";
import { ChevronDownIcon, DownloadIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { downloadRoundScopedFile } from "@client/lib/downloadRoundScopedFile";
import { toast } from "sonner";
import { Onboarding } from "../../components/wizard/Onboarding";
import { resolveHomepageView } from "./resolveHomepageView";
import { RoundActionButton } from "./RoundActionButton";

const DownloadRoundButton = withErrorBoundary(function DownloadRoundButton({
  roundId,
  roundName,
}: {
  roundId: string;
  roundName: string;
}) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownload(e: React.MouseEvent) {
    e.preventDefault();
    setDownloading(true);

    try {
      const result = await downloadRoundScopedFile({
        path: `/api/download-round?roundId=${encodeURIComponent(roundId)}`,
        filename: `${roundName}_database.json`,
      });
      if (!result.ok) {
        toast.error(result.error);
      }
    } finally {
      setDownloading(false);
    }
  }

  return (
    <RoundActionButton isLoading={downloading} onClick={handleDownload}>
      <DownloadIcon />
      Download
    </RoundActionButton>
  );
});

// Admin-only quick links to jump straight into a round's Proposals page and
// funder Review tab from the homepage.
function RoundAdminShortcuts({ roundId }: { roundId: string }) {
  return (
    <>
      <RoundActionButton href={`/round/${roundId}/proposals`}>
        Proposals
      </RoundActionButton>
      <RoundActionButton href={`/round/${roundId}/funder/review`}>
        Review
      </RoundActionButton>
    </>
  );
}

// One row of a round list. Both lists on this page — the rounds you are in and
// the admin-only "rounds you are not participating in" — render through this so
// the row hover and action styling can only be defined once.
function RoundListRow({
  roundId,
  roundName,
  archivedAt,
  showAdminShortcuts,
  showDownload,
}: {
  roundId: string;
  roundName: string;
  archivedAt?: Date | string | null;
  showAdminShortcuts: boolean;
  showDownload: boolean;
}) {
  return (
    <li className="group even:bg-muted hover:bg-surface-hover border-b last:border-b-0 flex items-center gap-1.5 pointer-coarse:gap-2 pr-3">
      <a
        className="flex-1 min-w-0 px-4 py-3 font-medium flex items-baseline gap-2 flex-wrap"
        href={`/round/${roundId}/`}
      >
        {roundName}
        {archivedAt != null && (
          <span className="text-xs font-normal text-muted-foreground">
            Archived{" "}
            {new Date(archivedAt).toLocaleDateString(undefined, {
              dateStyle: "medium",
            })}
          </span>
        )}
      </a>
      {showAdminShortcuts && <RoundAdminShortcuts roundId={roundId} />}
      {showDownload && (
        <DownloadRoundButton roundId={roundId} roundName={roundName} />
      )}
    </li>
  );
}

function AdminDemoRoundTool() {
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [needsName, setNeedsName] = useState(false);
  const [lastCreatedRound, setLastCreatedRound] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const createDemoRound = trpc.createDemoRound.useMutation({
    onSuccess: async (result) => {
      if (result.status === "needsName") {
        setNeedsName(true);
        toast.error("Enter a name for this demo user and submit again.");
        return;
      }

      setLastCreatedRound({
        id: result.roundId,
        name: result.roundName,
      });
      setEmail("");
      setName("");
      setNeedsName(false);
      toast.success(`Created ${result.roundName}`);
      await utils.getHomepageData.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create demo round");
    },
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const normalizedEmail = email.trim();
    const normalizedName = name.trim();

    if (normalizedEmail.length === 0) {
      toast.error("Email is required");
      return;
    }

    createDemoRound.mutate({
      email: normalizedEmail,
      name: needsName ? normalizedName || undefined : undefined,
    });
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="p-0 overflow-hidden">
        <CollapsibleTrigger
          render={
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-accent"
            />
          }
        >
          <div>
            <h2 className="text-sm font-medium">Admin demo round tool</h2>
            <p className="text-xs text-muted-foreground">
              Manually create a demo round for a user.
            </p>
          </div>
          <ChevronDownIcon
            className={`size-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <form
            className="border-t px-4 py-4 flex flex-col gap-3"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="demo-round-email" className="text-sm font-medium">
                User Email
              </label>
              <p className="text-xs text-muted-foreground">
                Enter the email for the user you are creating this demo round
                for.
              </p>
              <Input
                id="demo-round-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setNeedsName(false);
                }}
                placeholder="user@example.com"
                required
              />
            </div>

            {needsName && (
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="demo-round-name"
                  className="text-sm font-medium"
                >
                  Name
                </label>
                <Input
                  id="demo-round-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="User Name"
                  required
                />
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button type="submit" isLoading={createDemoRound.isPending}>
                Create Test Round
              </Button>
              {lastCreatedRound && (
                <a
                  href={`/round/${lastCreatedRound.id}/`}
                  className="text-sm underline"
                >
                  Open {lastCreatedRound.name}
                </a>
              )}
            </div>
          </form>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Archived rounds from both lists, collapsed at the bottom of the page. They
// stay reachable (an archived round is read-only, not gone) without crowding
// the rounds people are actually working in. Restoring happens inside the
// round, in Round Settings, so there is one restore path.
function ArchivedRounds({
  rounds,
  isSfcAdmin,
}: {
  rounds: { id: string; name: string; archivedAt: Date | string | null }[];
  isSfcAdmin: boolean;
}) {
  const [open, setOpen] = useLocalStorage<boolean>(
    "homepageShowArchivedRounds",
    false,
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="py-0 overflow-hidden">
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
          <div>
            <h2 className="text-sm font-medium">
              Archived rounds ({rounds.length})
            </h2>
            <p className="text-xs text-muted-foreground">
              Read-only until restored from their Round Settings.
            </p>
          </div>
          <ChevronDownIcon
            className={`size-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="border-t">
            {rounds.map((round) => (
              <RoundListRow
                key={round.id}
                roundId={round.id}
                roundName={round.name}
                archivedAt={round.archivedAt}
                showAdminShortcuts={isSfcAdmin}
                showDownload={false}
              />
            ))}
          </ul>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Admin-only list of all rounds the viewer is not a member of, with a toggle
// to hide demo rounds (identified by name — they have no DB flag) since they
// quickly outnumber the real rounds.
function NonParticipatingRounds({
  rounds,
  offline,
}: {
  rounds: { id: string; name: string }[];
  offline: boolean;
}) {
  const [showDemoRounds, setShowDemoRounds] = useLocalStorage<boolean>(
    "homepageShowDemoRounds",
    false,
  );

  const demoRoundCount = rounds.filter((round) =>
    isDemoRoundName(round.name),
  ).length;
  const visibleRounds = showDemoRounds
    ? rounds
    : rounds.filter((round) => !isDemoRoundName(round.name));

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold">
          ADMIN: Rounds you are not participating in
        </h2>
        {demoRoundCount > 0 && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={showDemoRounds}
              onCheckedChange={(checked) => setShowDemoRounds(checked === true)}
            />
            Show demo rounds ({demoRoundCount})
          </label>
        )}
      </div>
      {visibleRounds.length > 0 && (
        <Card className="py-0 overflow-hidden">
          <ul>
            {visibleRounds.map((round) => (
              <RoundListRow
                key={round.id}
                roundId={round.id}
                roundName={round.name}
                showAdminShortcuts={true}
                showDownload={!offline}
              />
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

export function Home() {
  const query = trpc.getHomepageData.useQuery();
  type HomepageData = NonNullable<typeof query.data>;

  // getHomepageData is anonymized per the *effective* user (ctx.getUser()), so
  // its offline cache must be scoped to that user — otherwise an admin who
  // cached the page while impersonating could be served that user's round list
  // offline. The effective user is the impersonated user when impersonating,
  // else the underlying logged-in user (offline-stable via useCurrentUser).
  const { data: currentUser } = useCurrentUser();
  const impersonation = useImpersonationRefValue();
  const effectiveUserId = impersonation ?? currentUser?.userId ?? null;

  // Mirror the homepage into the iPad offline cache on success, and serve it
  // back when offline so a funder can still navigate to a cached round. Only
  // write once the query has refetched for the CURRENT effective user:
  // getHomepageData reuses the same React Query key across an impersonation
  // switch (the viewer rides in a header), so right after a switch `query.data`
  // still holds the previous viewer's round list. `query.data.user.id` is the
  // viewer the payload is for — gating on it prevents writing stale data under
  // the new user's key (which they could then see offline).
  useEffect(() => {
    if (
      query.data?.user?.id != null &&
      query.data.user.id === effectiveUserId
    ) {
      writeOfflineCacheIfIPad(
        OFFLINE_CACHE_KEYS.homepageData(effectiveUserId),
        query.data,
      );
    }
  }, [query.data, effectiveUserId]);

  // `error ?? failureReason`, because a refetch resets a dataless errored
  // query to pending with `error` cleared (SP-2612) — `failureReason` still
  // carries the in-flight attempt's transport error, so the cached round list
  // keeps rendering instead of flapping back to the loading skeleton.
  const queryFailure = query.error ?? query.failureReason;
  const offline =
    isIPadOffline() ||
    (queryFailure != null && isIPadOfflineFailure(queryFailure));
  const cachedData =
    offline && effectiveUserId != null
      ? readOfflineCache<HomepageData>(
          OFFLINE_CACHE_KEYS.homepageData(effectiveUserId),
        )
      : null;
  // Scope the rendered data to the current effective user (never expose another
  // viewer's payload from the shared query key), prefer the offline cache when
  // the live data isn't usable, and resolve loading/error without spinning
  // forever on a failed post-impersonation refetch. Pure + unit-tested.
  const { data, isLoading, isError } = resolveHomepageView<HomepageData>({
    queryData: query.data,
    cachedData,
    effectiveUserId,
    isQueryLoading: query.isLoading,
    isQueryError: query.isError,
  });

  const visibleRounds = [
    ...(data?.user?.participating_in ?? []),
    ...(data?.nonParticipatingRounds ?? []).map((round) => ({
      round,
      track: "NO_TRACK",
    })),
  ];
  // Archived rounds leave both lists and gather in one collapsed section.
  const liveParticipatingIn = (data?.user?.participating_in ?? []).filter(
    ({ round }) => round.archivedAt == null,
  );
  const liveNonParticipating = (data?.nonParticipatingRounds ?? []).filter(
    (round) => round.archivedAt == null,
  );
  const archivedRounds = visibleRounds
    .map(({ round }) => round)
    .filter((round) => round.archivedAt != null);

  if (!isError && !isLoading && visibleRounds.length === 0) {
    if (data?.user?.isSfcAdmin) {
      return (
        <Onboarding
          isSfcAdmin={true}
          before={
            <div className="px-4 pt-6 md:px-8">
              <AdminDemoRoundTool />
            </div>
          }
        />
      );
    }

    return <Onboarding isSfcAdmin={data?.user?.isSfcAdmin ?? false} />;
  }

  return (
    <Page
      title="Rounds you are participating in"
      className="flex flex-col gap-6"
      cta={
        data?.user &&
        !offline && (
          <Dialog>
            <DialogTrigger
              render={<Button className="self-end md:self-auto" />}
            >
              Create Round
            </DialogTrigger>
            <DialogContent className="bg-background">
              <DialogTitle className="text-center">Create Round</DialogTitle>
              <RoundWizard isSfcAdmin={data.user.isSfcAdmin} />
            </DialogContent>
          </Dialog>
        )
      }
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : isError || !data?.user ? (
        <p className="text-muted-foreground">
          An error occurred while loading the homepage. Please try refreshing
          the page.
        </p>
      ) : (
        <>
          {data.user.isSfcAdmin && <AdminDemoRoundTool />}

          {liveParticipatingIn.length > 0 && (
            <Card className="py-0 overflow-hidden">
              <ul>
                {liveParticipatingIn.map(({ round, track }) => (
                  <RoundListRow
                    key={round.id}
                    roundId={round.id}
                    roundName={round.name}
                    showAdminShortcuts={data.user?.isSfcAdmin ?? false}
                    showDownload={
                      !offline &&
                      (track === "NO_TRACK" || !!data.user?.isSfcAdmin)
                    }
                  />
                ))}
              </ul>
            </Card>
          )}

          {liveNonParticipating.length > 0 && (
            <NonParticipatingRounds
              rounds={liveNonParticipating}
              offline={offline}
            />
          )}

          {archivedRounds.length > 0 && (
            <ArchivedRounds
              rounds={archivedRounds}
              isSfcAdmin={data.user.isSfcAdmin}
            />
          )}
        </>
      )}
    </Page>
  );
}
